import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output
} from '@angular/core';

import { IVbSource } from 'vbrick-player-src/IVbSource';
import { VideogularComponent } from 'vbrick-player-src/videogular/Videogular.Component';

import { IOnHlsLevelsChangedData } from '../IOnHlsLevelsChangedData';
import { buildMainPlayerLevelsChangedData } from '../HlsLevelsUtils';

import { ILevelsUpdated } from './ILevelsUpdatedData';
import { LEVELS_UPDATED } from './DualPlaybackOrchestrator';
import { VgConferenceDualHlsService } from './VgConferenceDualHls.Service';
import { buildLevelsChangedDataForPlayer } from '../HlsLevelsUtils';

@Component({
	selector: 'vg-conference-dual-hls',
	changeDetection: ChangeDetectionStrategy.OnPush,
	template: ``
})
export class VgConferenceDualHlsComponent implements OnInit {
	@Input() public contentPlayerContainer: HTMLElement;

	@Output() public onHlsLevelsChanged: EventEmitter<IOnHlsLevelsChangedData> = new EventEmitter();

	private playbackOrchestrator: import('./DualPlaybackOrchestrator').DualPlaybackOrchestrator;

	constructor(
		private vgApi: VideogularComponent,
		private vgConferenceDualHlsService: VgConferenceDualHlsService
	) {}

	public ngOnInit(): void {
		this.vgApi.registerPlaybackPlugin((source: IVbSource) => this.loadPlugin(source));
	}

	public loadPlugin(source: IVbSource): false | (() => void) {
		const { isLive, revConnectInfo, src, type } = source;
		const realSrc: string = this.vgConferenceDualHlsService.getRealSrc(src);
		const isMulticast: boolean = this.vgConferenceDualHlsService.isMulticast(src, type);

		if (this.vgConferenceDualHlsService.isPlaybackSupported(source)) {
			import(/* webpackChunkName: "DualPlaybackOrchestrator" */ './DualPlaybackOrchestrator')
				.then(importResult => {
					this.playbackOrchestrator = new importResult.DualPlaybackOrchestrator(realSrc, this.contentPlayerContainer, isLive, this.vgApi, revConnectInfo, isMulticast);

					this.playbackOrchestrator.on(LEVELS_UPDATED, (data: ILevelsUpdated) => this.onEmitLevels(data));
				});

			this.vgApi.isDualPlayback = true;
			this.vgApi.isForceHtmlSubtitleRendering = true;
			this.vgApi.ignoreDomErrors = true;

			// ignore decoding errors to allow for the plugin to instead recover
			this.vgApi.isIgnoreDecodingErrors = isLive; // TODO: extend to VOD

			//expose analytics data on API
			Object.defineProperties(this.vgApi, {
				bandwidth: {
					configurable: true,
					get: () => this.playbackOrchestrator.bandwidth
				},

				bitrate: {
					configurable: true,
					get: () => this.playbackOrchestrator.bitrate
				}
			});

			return () => this.unloadPlugin();
		}

		return false;
	}

	public unloadPlugin() {
		this.vgApi.isDualPlayback =
			this.vgApi.isDualPlaybackActive =
			this.vgApi.isForceHtmlSubtitleRendering =
			this.vgApi.ignoreDomErrors =
			this.vgApi.isIgnoreDecodingErrors =
			false;

		if (this.playbackOrchestrator) {
			this.playbackOrchestrator.destroy();
			this.playbackOrchestrator = undefined;
		}

		this.onHlsLevelsChanged.emit(null);

		//clean up custom accessors on the API
		Object.defineProperties(this.vgApi, {
			bandwidth: { value: null },
			bitrate: { value: null }
		});
	}

	private onEmitLevels(data: ILevelsUpdated): void {
		const payload: IOnHlsLevelsChangedData = buildMainPlayerLevelsChangedData(data.speakerPlayerLevels);

		if (data.contentPlayerLevels) {
			payload.players.push(
				buildLevelsChangedDataForPlayer('Content Player Level', data.contentPlayerLevels)
			);
		}

		this.onHlsLevelsChanged.emit(payload);
	}
}
